import { Box, Breadcrumbs, Typography } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import RightArrowIcon from '@mui/icons-material/ChevronRightRounded';
import { ROUTES } from 'constants/routes';
import FieldBox from 'shared/components/widgets/field-box';
import FieldBoxProperty from 'shared/components/widgets/field-box-property';
import Input from 'shared/components/widgets/input';
import Button from 'shared/components/widgets/button';
import RouteLink from 'shared/components/widgets/route-link';
import InputContainer from 'shared/components/widgets/input-container';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import { Controller, useForm } from 'react-hook-form';
import { systemPreferenceDetailService } from '@services/private/system/preference/preference-detail.service';
import { useEffect, useState } from 'react';
import { is200xRange } from '@utils/utilities';
import { systemPreferenceUpdateService } from '@services/private/system/preference/preference-update.service';
import { useNavigate } from 'react-router-dom';

type PreferenceData = {
  adminConfigEnv: {
    cssJsVersion: string;
  };
  configEnvDetail: {
    awsS3DownloadUrlExpireMinute: string;
    cssJsVersion: string;
  };
} | null;

const PreferencesPage = () => {
  const { S3_DOWNLOAD_URL_VALUE, VERSION } =
    VALIDATION_MESSAGE.SYSTEM.PREFERENCES;

  const [_preferenceData, setPreferenceData] = useState<PreferenceData>();

  const preferencesSchema = yup.object({
    cssJsVersion: yup
      .string()
      .required(VERSION.REQUIRED)
      .min(3, VERSION.MIN_LENGTH)
      .max(20, VERSION.MAX_LENGTH)
      .matches(/^[a-zA-Z0-9]+$/, VERSION.ONLY_UPPER_LOWER_NUM),

    awsS3DownloadUrlExpireMinute: yup
      .string()
      .required(S3_DOWNLOAD_URL_VALUE.REQUIRED)
      .min(1, S3_DOWNLOAD_URL_VALUE.MIN_LENGTH)
      .max(4, S3_DOWNLOAD_URL_VALUE.MAX_LENGTH),
    // .typeError(S3_DOWNLOAD_URL_VALUE.ONLY_NUM),
  });

  type PreferencesFormData = yup.InferType<typeof preferencesSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<PreferencesFormData>({
    defaultValues: {
      awsS3DownloadUrlExpireMinute: '',
      cssJsVersion: ''
    },
    resolver: yupResolver(preferencesSchema),
  });

  const navigate = useNavigate();

  const fetchPreferencesDetail = async () => {
    const { response, statusCode, error } =
      await systemPreferenceDetailService();

    if (error || !is200xRange(statusCode)) {
      navigate(ROUTES.CUSTOMER_DELIVERY_FILES.ROOT)
    }

    if (response?.code === '1' && is200xRange(statusCode)) {
      setPreferenceData(response?.result);
      setValue('cssJsVersion', response?.result?.configEnvDetail?.cssJsVersion);
      setValue(
        'awsS3DownloadUrlExpireMinute',
        response?.result?.configEnvDetail?.awsS3DownloadUrlExpireMinute,
      );
    }
  };

  const updatePreferences = async (
    cssJsVersion,
    awsS3DownloadUrlExpireMinute,
  ) => {
    const { response, statusCode } = await systemPreferenceUpdateService({
      cssJsVersion,
      awsS3DownloadUrlExpireMinute,
    });

    if (response?.code === '1' && is200xRange(statusCode)) {
    }
  };

  const onSubmit = (data: any) => {
    const { cssJsVersion, awsS3DownloadUrlExpireMinute } = data;
    updatePreferences(cssJsVersion, awsS3DownloadUrlExpireMinute);
  };

  useEffect(() => {
    fetchPreferencesDetail();
  }, []);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <Box marginBottom="10px">
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<RightArrowIcon sx={{ color: 'var(--neutral-800)' }} />}
        >
          <RouteLink to={ROUTES.SYSTEM.PREFERENCES.path}>Home</RouteLink>
          <RouteLink to={ROUTES.SYSTEM.PREFERENCES.path}>시스템</RouteLink>
          <RouteLink to={ROUTES.SYSTEM.PREFERENCES.path}>환경설정</RouteLink>
        </Breadcrumbs>
      </Box>
      <Box display="flex" flexDirection="column" marginBottom="20px">
        <Typography variant="h6" color="var(--base-black)">
          환경설정
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap="50px"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box display="flex" flexDirection="column" gap={6}>
          {/* BASIC SETTING */}
          <FieldBox title="기본 설정">
            {/* CSS, JS UPDATE CODE */}
            <FieldBoxProperty
              name="* CSS, JS 갱신용코드"
              value={
                <Box display="flex" flexDirection="column" gap="6px">
                  <Controller
                    name="cssJsVersion"
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputContainer type="form">
                          <Input
                            {...field}
                            placeholder="CSS, JS 버전을 입력해 주세요."
                            showErrorMsg={!!errors.cssJsVersion}
                            errorMessage={
                              errors.cssJsVersion && errors.cssJsVersion.message
                            }
                            value={field.value}
                          />
                        </InputContainer>
                      );
                    }}
                  />

                  <Typography variant="body2" color="var(--neutral-900)">
                    {`※ Browser cache 를 Reload 할 목적으로 사용.  (ex) lfs.css?v={CSS,JS 갱신용코드}`}
                  </Typography>
                </Box>
              }
            />
          </FieldBox>
          {/* AWS S3 SETTINGS */}
          <FieldBox title="AWS S3 설정">
            {/* S3 DOWNLOAD URL RETENTION (MINUTES) SETTING */}
            <FieldBoxProperty
              name="S3다운로드URL 유지(분) 설정"
              value={
                <Controller
                  name="awsS3DownloadUrlExpireMinute"
                  control={control}
                  render={({ field }) => {
                    return (
                      <InputContainer type="form">
                        <Input
                          {...field}
                          placeholder="S3다운로드URL 유지(분)값을  입력해 주세요."
                          showErrorMsg={!!errors.cssJsVersion}
                          errorMessage={
                            errors.awsS3DownloadUrlExpireMinute &&
                            errors.awsS3DownloadUrlExpireMinute.message
                          }
                          value={field.value}
                        />
                      </InputContainer>
                    );
                  }}
                />
              }
            />
          </FieldBox>
        </Box>
        {/* SAVE */}
        <Box>
          <Button variant="contained" color="primary" type="submit">
            저장하기
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PreferencesPage;
